// The errors services/api.ts reports when the server gives no message of its own
const en = {
  loginFailed: 'Login failed',
  wrongCredentials: 'Wrong email or password.',
  invalidFestivalId: "Check the festival ID: it's a code like 1b9d6bcd-bbfd-4b2d-9b5d-ab8dfbbd4bed.",
  unknownFestival: 'No festival has this ID.',
  unknownAccountEmail: 'No account uses this email for this festival.',
  tooManyAttempts: 'Too many attempts. Wait a minute, then try again.',
  wrongMasterKey: 'Wrong master key.',
  fetchVersionsFailed: 'Failed to fetch versions.',
  updateFestivalDataFailed: 'Failed to update festival data.',
  uploadImageFailed: 'Failed to upload image.',
  fetchCurrentVersionFailed: 'Failed to fetch current version.',
  versionNotFound: 'Version not found',
  /** With the server's message after it, when it gave one */
  createUserFailed: (message?: string) => message ? `Failed to create user: ${message}` : 'Failed to create user',
  /** How createUserFailed starts when the server gave a message, to tell that error apart */
  createUserFailedPrefix: 'Failed to create user:',
  createFestivalFailed: 'Failed to create festival',
  updateFirebaseSecretFailed: 'Failed to update Firebase secret',
  fetchRevisionsFailed: 'Failed to fetch revisions',
  revertFailed: 'Failed to revert to revision',
  sendNotificationFailed: 'Failed to send notification.',
  fetchFestivalsFailed: 'Failed to fetch festivals',
  fetchRevisionFailed: 'Failed to fetch revision details',
  fetchUsersFailed: (message?: string) => message ? `Failed to fetch users: ${message}` : 'Failed to fetch users',
  updateRolesFailed: (message?: string) => message ? `Failed to update roles: ${message}` : 'Failed to update roles',
  fetchUserFailed: (message?: string) => message ? `Failed to fetch user data: ${message}` : 'Failed to fetch user data',
  saveLanguageFailed: (status: number) => `Failed to save the language: ${status}`,
  deleteUserFailed: (message?: string) => message ? `Failed to delete user: ${message}` : 'Failed to delete user',
  resetPasswordFailed: 'Failed to reset password',
  recoveryCodeFailed: 'Failed to generate recovery code',
  updatePasswordFailed: 'Failed to update password',
  migrationsFailed: 'Failed to perform migrations',
  festivalIdFailed: 'Failed to fetch festival ID from subdomain',
  fetchCarpoolsFailed: 'Failed to fetch carpools',
  deleteCarpoolFailed: 'Failed to delete carpool',
  fetchReportsFailed: 'Failed to fetch reports',
  ignoreReportFailed: 'Failed to ignore report',
  applyReportFailed: 'Failed to apply report',
  fetchNewsFailed: 'Failed to fetch news',
  updateNewsFailed: 'Failed to update news',
  deleteNewsFailed: 'Failed to delete news item',
  fetchPagesFailed: 'Failed to fetch pages',
  updatePageFailed: 'Failed to update page',
  deletePageFailed: 'Failed to delete page',
  fetchAdsFailed: 'Failed to fetch advertisements',
  updateAdFailed: 'Failed to update advertisement',
  deleteAdFailed: 'Failed to delete advertisement',
  fetchTopicsFailed: 'Failed to fetch topics',
  saveTopicFailed: 'Failed to save topic',
  subdomainFailed: 'Failed to fetch subdomain',
  fetchScheduledFailed: 'Failed to fetch scheduled notifications',
  scheduleFailed: 'Failed to schedule notification.',
  updateScheduledFailed: 'Failed to update scheduled notification.',
  cancelScheduledFailed: 'Failed to cancel scheduled notification.',
};

const fr: typeof en = {
  loginFailed: 'Échec de la connexion',
  wrongCredentials: 'E-mail ou mot de passe incorrect.',
  invalidFestivalId: "Vérifiez l'identifiant du festival\u00a0: c'est un code comme 1b9d6bcd-bbfd-4b2d-9b5d-ab8dfbbd4bed.",
  unknownFestival: 'Aucun festival ne correspond à cet identifiant.',
  unknownAccountEmail: "Aucun compte n'utilise cet e-mail pour ce festival.",
  tooManyAttempts: 'Trop de tentatives. Patientez une minute, puis réessayez.',
  wrongMasterKey: 'Clé maître incorrecte.',
  fetchVersionsFailed: 'Impossible de charger les versions.',
  updateFestivalDataFailed: 'Impossible d\'enregistrer les données du festival.',
  uploadImageFailed: 'Impossible d\'importer l\'image.',
  fetchCurrentVersionFailed: 'Impossible de charger la version.',
  versionNotFound: 'Version introuvable',
  createUserFailed: (message?: string) =>
    message ? `Impossible de créer l'utilisateur\u00a0: ${message}` : 'Impossible de créer l\'utilisateur',
  createUserFailedPrefix: 'Impossible de créer l\'utilisateur\u00a0:',
  createFestivalFailed: 'Impossible de créer le festival',
  updateFirebaseSecretFailed: 'Impossible de mettre à jour la clé secrète Firebase',
  fetchRevisionsFailed: 'Impossible de charger l\'historique',
  revertFailed: 'Impossible de rétablir l\'état d\'avant cette modification',
  sendNotificationFailed: 'Impossible d\'envoyer la notification.',
  fetchFestivalsFailed: 'Impossible de charger les festivals',
  fetchRevisionFailed: 'Impossible de charger le détail de la modification',
  fetchUsersFailed: (message?: string) =>
    message ? `Impossible de charger les utilisateurs\u00a0: ${message}` : 'Impossible de charger les utilisateurs',
  updateRolesFailed: (message?: string) =>
    message ? `Impossible de mettre à jour les rôles\u00a0: ${message}` : 'Impossible de mettre à jour les rôles',
  fetchUserFailed: (message?: string) =>
    message ? `Impossible de charger les données de l'utilisateur\u00a0: ${message}` : 'Impossible de charger les données de l\'utilisateur',
  saveLanguageFailed: (status: number) => `Impossible d'enregistrer la langue\u00a0: ${status}`,
  deleteUserFailed: (message?: string) =>
    message ? `Impossible de supprimer l'utilisateur\u00a0: ${message}` : 'Impossible de supprimer l\'utilisateur',
  resetPasswordFailed: 'Impossible de réinitialiser le mot de passe',
  recoveryCodeFailed: 'Impossible de générer le code de récupération',
  updatePasswordFailed: 'Impossible de modifier le mot de passe',
  migrationsFailed: 'Impossible d\'exécuter les migrations',
  festivalIdFailed: 'Impossible de trouver l\'identifiant du festival à partir du sous-domaine',
  fetchCarpoolsFailed: 'Impossible de charger les covoiturages',
  deleteCarpoolFailed: 'Impossible de supprimer le covoiturage',
  fetchReportsFailed: 'Impossible de charger les signalements',
  ignoreReportFailed: 'Impossible d\'ignorer le signalement',
  applyReportFailed: 'Impossible d\'appliquer le signalement',
  fetchNewsFailed: 'Impossible de charger les actus',
  updateNewsFailed: 'Impossible d\'enregistrer l\'actu',
  deleteNewsFailed: 'Impossible de supprimer l\'actu',
  fetchPagesFailed: 'Impossible de charger les pages',
  updatePageFailed: 'Impossible d\'enregistrer la page',
  deletePageFailed: 'Impossible de supprimer la page',
  fetchAdsFailed: 'Impossible de charger les publicités',
  updateAdFailed: 'Impossible d\'enregistrer la publicité',
  deleteAdFailed: 'Impossible de supprimer la publicité',
  fetchTopicsFailed: 'Impossible de charger les sujets',
  saveTopicFailed: 'Impossible d\'enregistrer le sujet',
  subdomainFailed: 'Impossible de charger le sous-domaine',
  fetchScheduledFailed: 'Impossible de charger les notifications programmées',
  scheduleFailed: 'Impossible de programmer la notification.',
  updateScheduledFailed: 'Impossible de modifier la notification programmée.',
  cancelScheduledFailed: 'Impossible d\'annuler la notification programmée.',
};

const messages = { en, fr };
export default messages;
